// src/App.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <motion.nav 
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="nav-container">
          <div className="logo">
            <motion.div 
              className="logo-icon"
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 50 50" fill="none">
                <path d="M25 5L45 15V35L25 45L5 35V15L25 5Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M25 15L35 20V30L25 35L15 30V20L25 15Z" fill="currentColor"/>
              </svg>
            </motion.div>
            <span>AuthentiChain</span>
          </div>
          
          <div className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
            <a onClick={() => scrollToSection('home')}>Home</a>
            <a onClick={() => scrollToSection('about')}>About</a>
            <a onClick={() => scrollToSection('features')}>Features</a>
            <a onClick={() => scrollToSection('how-it-works')}>How It Works</a>
            <a onClick={() => scrollToSection('testimonials')}>Testimonials</a>
            <a onClick={() => scrollToSection('contact')}>Contact</a>
            <button className="cta-button">Verify Document</button>
          </div>

          <button className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="blockchain-bg">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="chain-block"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.1, scale: 1 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
            />
          ))}
        </div>
        
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Blockchain-Powered
            <span className="gradient-text"> Document Verification</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Secure, Immutable, and Instant Certificate Authentication
          </motion.p>
          
          <motion.div 
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <button className="primary-btn">Get Started</button>
            <button className="secondary-btn">Learn More</button>
          </motion.div>

          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <div className="stat">
              <h3>10K+</h3>
              <p>Verified Documents</p>
            </div>
            <div className="stat">
              <h3>500+</h3>
              <p>Institutions</p>
            </div>
            <div className="stat">
              <h3>99.9%</h3>
              <p>Uptime</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>About <span className="gradient-text">AuthentiChain</span></h2>
            <p>Revolutionizing Document Verification with Blockchain Technology</p>
          </motion.div>

          <div className="about-content">
            <motion.div 
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3>Our Mission</h3>
              <p>
                AuthentiChain has built a unique ecosystem that includes all stakeholders 
                of documents on a single platform using state-of-the-art blockchain technology.
              </p>
              <p>
                In the era of digital identities, everyone has official documents that need 
                to be digitized and regularly consulted. However, they are far too rarely verified.
              </p>
              <p>
                To prevent fraud and guarantee the authenticity of digital credentials, 
                we've created an international standard of "verifiable credentials" that 
                ensures reliability and inviolability of information.
              </p>
            </motion.div>

            <motion.div 
              className="about-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="blockchain-visual">
                <motion.div 
                  className="block"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="block-face" style={{ transform: `rotateY(${i * 60}deg) translateZ(100px)` }}>
                      <svg viewBox="0 0 100 100">
                        <rect width="80" height="80" x="10" y="10" rx="10" fill="#e3f2fd"/>
                        <path d="M30 40 L50 50 L70 40" stroke="#1976d2" strokeWidth="3" fill="none"/>
                        <circle cx="50" cy="60" r="8" fill="#1976d2"/>
                      </svg>
                    </div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Powerful <span className="gradient-text">Features</span></h2>
            <p>Everything you need for secure document verification</p>
          </motion.div>

          <div className="features-grid">
            {[
              {
                icon: "🔒",
                title: "Immutable Records",
                description: "Once recorded on blockchain, documents cannot be altered or tampered with"
              },
              {
                icon: "⚡",
                title: "Instant Verification",
                description: "Verify certificates in seconds, not days or weeks"
              },
              {
                icon: "🌐",
                title: "Universal Access",
                description: "Access and share documents securely from anywhere in the world"
              },
              {
                icon: "💰",
                title: "Cost Effective",
                description: "Reduce verification costs by up to 70% compared to traditional methods"
              },
              {
                icon: "🛡️",
                title: "Fraud Prevention",
                description: "Eliminate fake certificates and forged documents completely"
              },
              {
                icon: "🔐",
                title: "Data Privacy",
                description: "Control who can access your documents with advanced encryption"
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(33, 150, 243, 0.3)" }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>How It <span className="gradient-text">Works</span></h2>
            <p>Simple, secure, and seamless verification process</p>
          </motion.div>

          <div className="steps">
            {[
              {
                step: "01",
                title: "Upload Document",
                description: "Institution uploads the certificate or document to the blockchain"
              },
              {
                step: "02",
                title: "Blockchain Storage",
                description: "Document hash is stored immutably on the blockchain network"
              },
              {
                step: "03",
                title: "Generate ID",
                description: "Unique verification ID is generated and linked to the document"
              },
              {
                step: "04",
                title: "Instant Verification",
                description: "Anyone can verify the document authenticity using the unique ID"
              }
            ].map((step, index) => (
              <motion.div 
                key={index}
                className="step"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Client <span className="gradient-text">Testimonials</span></h2>
            <p>What our users say about us</p>
          </motion.div>

          <div className="testimonials-grid">
            {[
              {
                name: "Dr. Sarah Johnson",
                role: "University Registrar",
                text: "AuthentiChain has revolutionized how we issue and verify degrees. The blockchain technology ensures complete authenticity and has reduced our verification time by 80%."
              },
              {
                name: "Michael Chen",
                role: "HR Director",
                text: "The platform has made our hiring process incredibly efficient. We can now verify candidate credentials in minutes instead of weeks. It's a game-changer for recruitment."
              },
              {
                name: "Emily Rodriguez",
                role: "Student",
                text: "I love having all my certificates on AuthentiChain. I can share them instantly with potential employers, and they know they're legitimate. It's given me a competitive edge."
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="quote-icon">"</div>
                <p>{testimonial.text}</p>
                <div className="testimonial-author">
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Get In <span className="gradient-text">Touch</span></h2>
            <p>Have questions? We'd love to hear from you</p>
          </motion.div>

          <div className="contact-content">
            <motion.div 
              className="contact-info"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="info-item">
                <div className="info-icon">📧</div>
                <div>
                  <h4>Email</h4>
                  <p>info@authentichain.live</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📱</div>
                <div>
                  <h4>Phone</h4>
                  <p>+971 55 519 2507</p>
                  <p>+91 76003 83828</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🌐</div>
                <div>
                  <h4>Website</h4>
                  <p>www.authentichain.live</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📍</div>
                <div>
                  <h4>Location</h4>
                  <p>Dubai, UAE</p>
                </div>
              </div>
            </motion.div>

            <motion.form 
              className="contact-form"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <input type="text" placeholder="Your Name" required />
              <input type="email" placeholder="Your Email" required />
              <input type="text" placeholder="Subject" required />
              <textarea placeholder="Your Message" rows="5" required></textarea>
              <button type="submit" className="primary-btn">Send Message</button>
            </motion.form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <div className="logo-icon">
                  <svg viewBox="0 0 50 50" fill="none">
                    <path d="M25 5L45 15V35L25 45L5 35V15L25 5Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M25 15L35 20V30L25 35L15 30V20L25 15Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>AuthentiChain</span>
              </div>
              <p>Securing documents with blockchain technology for a fraud-free future.</p>
            </div>

            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a onClick={() => scrollToSection('home')}>Home</a></li>
                <li><a onClick={() => scrollToSection('about')}>About</a></li>
                <li><a onClick={() => scrollToSection('features')}>Features</a></li>
                <li><a onClick={() => scrollToSection('contact')}>Contact</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Services</h4>
              <ul>
                <li><a href="#">Document Verification</a></li>
                <li><a href="#">Certificate Issuance</a></li>
                <li><a href="#">Institutional Services</a></li>
                <li><a href="#">API Integration</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <ul>
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
                <li><a href="#">Cookie Policy</a></li>
                <li><a href="#">Disclaimer</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 AuthentiChain. All rights reserved.</p>
            <div className="social-links">
              <a href="#" aria-label="LinkedIn">LinkedIn</a>
              <a href="#" aria-label="Twitter">Twitter</a>
              <a href="#" aria-label="Facebook">Facebook</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
